import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StaffService } from '../api/services/staffService';

const deactivationReasons = [
    'Resigned',
    'Terminated',
    'Retired',
    'Redundancy',
    'Other'
];

export default function DeactivateStaffModal({ open, onClose, staff, staffData, onSuccess }) {
    const targetStaff = staff || staffData;
    const [reason, setReason] = useState('Resigned');
    const [lastWorkingDate, setLastWorkingDate] = useState(new Date().toISOString().split('T')[0]);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setReason('Resigned');
            setLastWorkingDate(new Date().toISOString().split('T')[0]);
            setError('');
            setIsDeactivating(false);
        }
    }, [open]);

    const handleDeactivate = async () => {
        if (!targetStaff) {
            setError('No staff member selected for deactivation.');
            return;
        }

        if (!reason) {
            setError('Please select a deactivation reason.');
            return;
        }

        if (!lastWorkingDate) {
            setError('Please select the last working date.');
            return;
        }
        
        setIsDeactivating(true);
        setError('');
        try {
            const payload = {
                reason,
                lastWorkingDate: lastWorkingDate // API expects YYYY-MM-DD
            };
            
            const staffId = targetStaff.id || targetStaff._id;
            await StaffService.deactivateStaff(staffId, payload);
            if (onSuccess) {
                onSuccess(`Staff member ${targetStaff.fullName || targetStaff.name || 'Staff'} has been deactivated.`, staffId, 'Inactive');
            }
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to deactivate staff member.');
        } finally {
            setIsDeactivating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontWeight: 'bold' }}>
                <WarningAmberIcon color="error" /> Deactivate Staff Member
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You are about to deactivate <strong>{targetStaff?.fullName || targetStaff?.name}</strong>. Please provide the reason and last working date for record-keeping.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Deactivation Reason</InputLabel>
                        <Select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            label="Deactivation Reason"
                        >
                            {deactivationReasons.map(r => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Last Working Date"
                        type="date"
                        fullWidth
                        size="small"
                        value={lastWorkingDate}
                        onChange={(e) => setLastWorkingDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                <Button onClick={onClose} color="inherit" disabled={isDeactivating}>Cancel</Button>
                <Button 
                    onClick={handleDeactivate} 
                    color="error" 
                    variant="contained"
                    disabled={isDeactivating}
                    startIcon={isDeactivating && <CircularProgress size={16} color="inherit" />}
                >
                    {isDeactivating ? 'Deactivating...' : 'Confirm Deactivation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
